import PropTypes from "prop-types";
import React, { memo } from "react";

import styled from "styled-components/macro";

import { FnrCombobox } from "UI/FnrSelectList3";

import SacramentSelectResult from "./SacramentFields/SacramentSelectResult";
import { cellContainerStyles } from "./cellContainerStyles";

const SelectListCell = memo(WrappedComponent);
export default SelectListCell;

WrappedComponent.propTypes = {
  columnID: PropTypes.number,
  entityID: PropTypes.number,
  value: PropTypes.string,
  rawValue: PropTypes.string,
  saveCellValue: PropTypes.func,
  canEdit: PropTypes.bool,
  isInitiallyRendered: PropTypes.bool,
  extendedFieldData: PropTypes.object,
  fieldTypeInstanceStructureGroupingID: PropTypes.number,
};

function WrappedComponent({
  columnID,
  entityID,
  value,
  rawValue,
  extendedFieldData,
  saveCellValue,
  canEdit,
  fieldTypeInstanceStructureGroupingID,
}) {
  const isSacramentField =
    fieldTypeInstanceStructureGroupingID === 1 ||
    extendedFieldData?.fieldTypeInstanceStructureGroupingID === 1;

  const fieldIsEditable = (() => {
    // This try/catch handles if rawValue is not a valid JSON string
    try {
      return JSON.parse(rawValue)?.isEditable ?? true;
    } catch {
      return true;
    }
  })();

  const isEditable = canEdit && !isSacramentField && fieldIsEditable;

  // In reality it's the same type but we call it different names in different places
  const isExtendedField = extendedFieldData?.type === "picker";

  if (!(isExtendedField && canEdit)) {
    // For Signup Items and read-only extended field selectLists
    return <ReadOnlyContainer title={value}>{value}</ReadOnlyContainer>;
  }

  let selected = [];
  if (typeof rawValue === "string" && rawValue.length > 0) {
    try {
      const { values } = JSON.parse(rawValue);
      if (Array.isArray(values) && values.length > 0) {
        for (let i = 0; i < values.length; ++i) {
          selected.push(Number(values[i].optionListValueID));
        }
      }
    } catch (e) {
      console.error(e);
    }
  }

  const isMultiSelect = Boolean(extendedFieldData?.multiSelect);

  let options = [];
  if (!isMultiSelect) {
    // Allow "clearing"/de-selecting via a "blank" option
    options.push({ id: -1, value: "" });
  }

  const numFieldOptions = extendedFieldData?.options?.length;
  if (typeof numFieldOptions === "number" && numFieldOptions > 0) {
    for (let i = 0; i < numFieldOptions; ++i) {
      const { ID, title } = extendedFieldData?.options[i] || {};
      options.push({
        id: ID,
        value: title,
      });
    }
  }

  const save = (selection) => {
    saveCellValue({
      columnID,
      entityID,
      value: transformValueToSaveShape(isMultiSelect ? selection : [selection]),
      origValue: transformValueToSaveShape(selected),
    });
  };

  if (isSacramentField && fieldIsEditable) {
    return (
      <SacramentSelectResult
        selection={selected}
        options={options}
        entityID={entityID}
        extendedFieldData={extendedFieldData}
      />
    );
  }

  return (
    <SelectList
      selection={isMultiSelect ? selected : selected[0]}
      onSelect={save}
      options={options}
      multiselect={isMultiSelect}
      openIndicator={false}
      isButtonContainerFullWidth
      disabled={!isEditable}
    />
  );
}

function transformValueToSaveShape(selections) {
  return {
    values: selections.map((id) => ({
      optionListValueID: id,
    })),
  };
}

const ReadOnlyContainer = styled.div`
  ${cellContainerStyles};
  overflow: hidden;
  text-overflow: ellipsis;
`;

// TODO pull out common "Input" styles
const SelectList = styled(FnrCombobox)`
  ${cellContainerStyles};
  font-family: "Open Sans", sans-serif;
  white-space: nowrap;
  text-align: left;
  appearance: none;
  border: 1px solid rgba(0, 0, 0, 0.05);
  background: none;
  width: 100%;
  height: 30px;
  box-sizing: border-box;
  outline: none;
  border-radius: 6px;
  transition: border 0.2s, background 0.2s;

  span {
    color: #384a5d !important;
  }

  &:focus {
    border: 1px solid #278dc1;
    background: rgba(39, 141, 193, 0.05);
  }

  &:disabled {
    border: none;
  }
`;
